import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import TimeSlotPicker from "@/components/TimeSlotPicker";
import CountStepper from "@/components/CountStepper";
import { RC_QUICK_PLACES } from "@/lib/places";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Plus, Trash2, ArrowRight, ArrowLeft, Sun, Moon, CheckCircle2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";

const TIMES = Array.from({ length: 48 }, (_, k) => {
  const h = String(Math.floor(k / 2)).padStart(2, "0");
  const m = k % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

const priceFor = (time) => {
  if (!time) return null;
  const hh = parseInt(time.split(":")[0], 10);
  return (hh < 8 || hh >= 21) ? 20 : 10;
};

const BNB_ADDRESS = "B&B Emmanuel, Traversa IV Maldariti 29b, 89131 Reggio Calabria, Italia";

const newTrip = () => ({ _key: `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, origin: "", destination: BNB_ADDRESS, date: "", time: "", passengers: 1, luggage: 0 });

export default function ShuttleRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("return");
  const minDate = searchParams.get("min_date") || ""; // YYYY-MM-DD
  const maxDate = searchParams.get("max_date") || "";
  const prefName = searchParams.get("name") || "";
  const prefEmail = searchParams.get("email") || "";
  const prefPhone = searchParams.get("phone") || "";
  // If returning to booking, this is "attached" mode: trips will be saved to sessionStorage
  // and the booking flow handles the final submit/Telegram/email.
  const isAttachedMode = !!returnUrl;
  const [name, setName] = useState(prefName);
  const [email, setEmail] = useState(prefEmail);
  const [phone, setPhone] = useState(prefPhone);
  const [bookingCode, setBookingCode] = useState("");
  const [notes, setNotes] = useState("");
  const [trips, setTrips] = useState(() => {
    // Restore previously saved trips if present (user returning to edit)
    try {
      const saved = sessionStorage.getItem("pending_shuttle_trips");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) { console.warn("Saved trips parse failed:", e); }
    return [{ ...newTrip(), date: minDate || "" }];
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const total = trips.reduce((s, t) => s + (priceFor(t.time) || 0), 0);

  const updateTrip = (i, patch) => setTrips((arr) => arr.map((t, j) => (i === j ? { ...t, ...patch } : t)));
  const addTrip = () => setTrips([...trips, { ...newTrip(), date: minDate || "" }]);
  const removeTrip = (i) => setTrips(trips.filter((_, j) => j !== i));

  const validateTrips = () => {
    for (let i = 0; i < trips.length; i++) {
      const t = trips[i];
      if (!t.origin || !t.destination || !t.date || !t.time) {
        return { ok: false, msg: `Tratta ${i + 1}: compila origine, destinazione, data e orario` };
      }
      if (minDate && t.date < minDate) {
        return { ok: false, msg: `Tratta ${i + 1}: la data ${t.date} è precedente al check-in (${minDate}). Correggi la data.` };
      }
      if (maxDate && t.date > maxDate) {
        return { ok: false, msg: `Tratta ${i + 1}: la data ${t.date} è successiva al check-out (${maxDate}). Correggi la data.` };
      }
    }
    return { ok: true };
  };

  const onSubmit = async () => {
    // Attached mode: save trips and return to booking step 3
    if (isAttachedMode) {
      const v = validateTrips();
      if (!v.ok) { toast.error(v.msg); return; }
      try {
        sessionStorage.setItem("pending_shuttle_trips", JSON.stringify(trips));
      } catch (e) { console.warn("pending_shuttle_trips save failed:", e); }
      toast.success("Richiesta navetta allegata alla prenotazione");
      navigate(`${returnUrl}${returnUrl.includes("?") ? "&" : "?"}step=3&shuttle_added=1`);
      return;
    }

    // Standalone mode: send directly
    if (!name.trim() || !email.trim()) {
      toast.error("Inserisci nome ed email");
      return;
    }
    const v = validateTrips();
    if (!v.ok) { toast.error(v.msg); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/shuttle-request", {
        name, email, phone: phone || null,
        booking_code: bookingCode || null,
        trips, notes: notes || null,
      });
      setSubmitted(r.data);
      toast.success("Richiesta inviata! Riceverai una conferma a breve.");
    } catch (err) {
      toast.error(errMsg(err, "Errore invio richiesta"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = isAttachedMode
    ? (submitting ? "Salvo..." : "✓ Allega alla prenotazione")
    : (submitting ? "Invio..." : "Invia richiesta");

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <Header />
        <main className="flex-1 max-w-2xl mx-auto px-4 py-16 w-full" data-testid="shuttle-success">
          <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center shadow-lg">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="font-display text-3xl text-[#1E3A5F] mb-2">Richiesta inviata</h1>
            <p className="text-[#635F59] mb-4">
              L'host è stato avvisato e ti contatterà a <strong>{email}</strong> appena possibile per confermare disponibilità e dettagli di pagamento.
            </p>
            <div className="inline-block bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg px-4 py-2 mb-6">
              <div className="text-[10px] uppercase tracking-wider text-[#635F59]">Totale stimato</div>
              <div className="font-display text-2xl text-[#AC5D45]">€{submitted.estimated_total}</div>
            </div>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => { setSubmitted(null); setTrips([newTrip()]); }} variant="outline" data-testid="btn-new-request">
                Nuova richiesta
              </Button>
              <Button onClick={() => navigate(returnUrl || "/")} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="btn-home">
                {returnUrl ? "← Torna alla prenotazione" : "Torna alla home"}
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full">
        {returnUrl && (
          <button
            onClick={() => navigate(returnUrl)}
            className="inline-flex items-center gap-2 text-sm text-[#1E3A5F] hover:text-[#AC5D45] mb-4 transition"
            data-testid="btn-return-booking"
          >
            <ArrowLeft className="w-4 h-4" /> Torna alla prenotazione
          </button>
        )}
        <div className="mb-8 text-center">
          <div className="inline-block px-3 py-1 bg-[#AC5D45]/10 text-[#AC5D45] text-xs font-semibold uppercase tracking-widest rounded-full mb-3">Servizio Navetta · su richiesta</div>
          <h1 className="font-display text-4xl text-[#1E3A5F]">Richiedi il transfer</h1>
          <p className="text-[#635F59] mt-2 max-w-xl mx-auto">
            {isAttachedMode
              ? "Le tratte verranno allegate alla tua prenotazione. L'host conferma via email quando approva."
              : "Compila il modulo: l'host verificherà la disponibilità e ti contatterà via email per confermare. Pagamento direttamente al conducente."}
          </p>
          {isAttachedMode && minDate && maxDate && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 text-blue-900 px-3 py-1.5 rounded-full">
              <Clock className="w-3.5 h-3.5" />
              Date ammesse: <b>{minDate}</b> → <b>{maxDate}</b> (range della prenotazione)
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          {/* Form */}
          <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-5 shadow-sm">
            {/* Contact section (hidden when attached to a booking, since data is already there) */}
            {!isAttachedMode && (
            <div>
              <h2 className="font-display text-xl text-[#1E3A5F] mb-3">I tuoi contatti</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nome e cognome <span className="text-rose-600">*</span></Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="sh-name" />
                </div>
                <div>
                  <Label>Email <span className="text-rose-600">*</span></Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="sh-email" />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" data-testid="sh-phone" />
                </div>
                <div>
                  <Label>Codice prenotazione (se ne hai una)</Label>
                  <Input value={bookingCode} onChange={(e) => setBookingCode(e.target.value)} className="mt-1.5" placeholder="EMM..." data-testid="sh-code" />
                </div>
              </div>
            </div>
            )}

            {/* Trips */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-xl text-[#1E3A5F]">Tratte richieste</h2>
                <span className="text-xs text-[#635F59]">
                  ☀️ 08:00–21:00 · <strong className="text-[#AC5D45]">€10</strong> · 🌙 21:00–08:00 · <strong className="text-[#AC5D45]">€20</strong>
                </span>
              </div>

              <div className="space-y-3">
                {trips.map((t, i) => {
                  const p = priceFor(t.time);
                  const hh = t.time ? parseInt(t.time.split(":")[0], 10) : null;
                  const isNight = hh !== null && (hh < 8 || hh >= 21);
                  return (
                    <div key={t._key || i} className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-xl p-4" data-testid={`sh-trip-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-[#1E3A5F]">
                          Tratta #{i + 1}
                          {p !== null && (
                            <span className="ml-2 text-[#AC5D45] font-medium">
                              {isNight ? <Moon className="w-3.5 h-3.5 inline -mt-0.5" /> : <Sun className="w-3.5 h-3.5 inline -mt-0.5" />}{" "}
                              {isNight ? "Notturna" : "Diurna"} · €{p}
                            </span>
                          )}
                        </div>
                        {trips.length > 1 && (
                          <button onClick={() => removeTrip(i)} className="text-rose-600 hover:text-rose-800" data-testid={`sh-remove-${i}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid sm:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Punto di partenza</Label>
                          <AddressAutocomplete
                            value={t.origin}
                            onChange={(v) => updateTrip(i, { origin: v })}
                            placeholder="Es. Aeroporto Reggio Calabria"
                            dataTestid={`sh-origin-${i}`}
                            className="mt-1"
                            quickChips={RC_QUICK_PLACES.slice(0, 5)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Punto di arrivo</Label>
                          <AddressAutocomplete
                            value={t.destination}
                            onChange={(v) => updateTrip(i, { destination: v })}
                            placeholder="Es. B&B Emmanuel, Reggio Calabria"
                            dataTestid={`sh-destination-${i}`}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Data</Label>
                          <DateInputIT min={minDate || undefined}
                            max={maxDate || undefined}
                            value={t.date}
                            onChange={(e) => updateTrip(i, { date: e.target.value })}
                            className="mt-1"
                            data-testid={`sh-date-${i}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Orario</Label>
                          <TimeSlotPicker
                            value={t.time}
                            onChange={(v) => updateTrip(i, { time: v })}
                            dataTestid={`sh-time-${i}`}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Passeggeri</Label>
                          <CountStepper
                            value={t.passengers}
                            onChange={(v) => updateTrip(i, { passengers: v })}
                            min={1}
                            max={8}
                            dataTestid={`sh-pax-${i}`}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Bagagli</Label>
                          <CountStepper
                            value={t.luggage}
                            onChange={(v) => updateTrip(i, { luggage: v })}
                            min={0}
                            max={8}
                            dataTestid={`sh-luggage-${i}`}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={addTrip}
                className="mt-3 w-full py-3 border-2 border-dashed border-[#AC5D45]/40 text-[#AC5D45] hover:bg-[#AC5D45]/5 rounded-xl text-sm font-semibold transition flex items-center justify-center gap-2"
                data-testid="sh-add-trip"
              >
                <Plus className="w-4 h-4" /> Aggiungi un'altra tratta
              </button>
            </div>

            {/* Notes */}
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Es. volo IB1234, seggiolino bambino, navetta con sedile extra-large..."
                rows={3}
                className="mt-1.5"
                data-testid="sh-notes"
              />
            </div>
          </div>

          {/* Sidebar recap */}
          <aside className="bg-gradient-to-br from-[#1E3A5F] to-[#15294A] text-white rounded-2xl p-6 lg:sticky lg:top-24 shadow-xl">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E3A857] font-bold mb-3">Riepilogo</div>
            <div className="space-y-2 text-sm mb-4">
              {trips.map((t, i) => {
                const p = priceFor(t.time);
                return (
                  <div key={t._key || i} className="border-b border-white/10 pb-2 last:border-0 last:pb-0">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <div className="text-white/60 text-[10px] uppercase">Tratta {i + 1}</div>
                        <div className="text-xs truncate">{t.origin || "—"} <ArrowRight className="w-3 h-3 inline" /> {t.destination || "—"}</div>
                        <div className="text-[11px] text-white/60">{t.date || "—"} · {t.time || "—"}</div>
                      </div>
                      <div className="font-display text-base whitespace-nowrap">{p !== null ? `€${p}` : "—"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-white/20 pt-4 flex justify-between items-center mb-5">
              <span className="text-sm">Totale stimato</span>
              <span className="font-display text-3xl text-[#E3A857]">€{total}</span>
            </div>
            <Button
              onClick={onSubmit}
              disabled={submitting}
              className="w-full bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-xl h-12 font-semibold"
              data-testid="sh-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {submitLabel}</> : submitLabel}
            </Button>
            <p className="text-[10px] text-white/60 mt-3 leading-relaxed">
              {isAttachedMode
                ? "Le tratte verranno allegate alla prenotazione. L'host le riceverà insieme alla prenotazione e confermerà disponibilità via email."
                : "L'host risponde via email entro poche ore. Pagamento direttamente al conducente."}
            </p>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
